// RetailMobileMap.js - Self-contained version using actual API endpoints
import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import AuthContext from '../../contexts/auth-context.js';
import Spinner from "../../components/atoms/Spinner.js";
import moment from 'moment-timezone';

const RetailMobileMap = () => {
  const { name } = useParams();
  const authCtx = useContext(AuthContext);
  
  // Local state for mobile map
  const [loading, setLoading] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [data, setData] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [loadedComponent, setLoadedComponent] = useState(null);
  const [hoveredZoneId, setHoveredZoneId] = useState(null);

  // Add this useEffect to your existing RetailMobileMap component
useEffect(() => {
    // Create style element to hide navigation
    const style = document.createElement('style');
    style.id = 'hide-nav-mobile-map';
    style.textContent = `
      /* Hide all navigation elements */
      nav, .navbar, .sidebar, .nav-sidebar, .navigation, 
      .nav-container, .nav-wrapper, .main-nav, .side-nav,
      [class*="nav"], [class*="sidebar"], [id*="nav"], [id*="sidebar"] {
        display: none !important;
      }
      
      /* Make content take full width */
      .main-content, .content, .page-content, #root > div {
        margin: 0 !important;
        padding: 0 !important;
        width: 100vw !important;
        max-width: none !important;
      }
      
      /* Ensure body and html are full screen */
      body, html {
        margin: 0 !important;
        padding: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        overflow: hidden !important;
      }
    `;
    
    document.head.appendChild(style);
    
    // Cleanup when component unmounts
    return () => {
      const styleElement = document.getElementById('hide-nav-mobile-map');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  // Get location from auth context based on name parameter
  useEffect(() => {
    if (authCtx.loggedIn && authCtx.authLocationData?.retail) {
      // Find location by name (convert URL param back to location)
      const location = authCtx.authLocationData.retail.find(loc => 
        loc.name.toLowerCase().replace(/\s+/g, '-') === name
      );
      
      if (location) {
        console.log('Found location from auth context:', location);
        setCurrentLocation(location);
      } else {
        console.error('Location not found for name:', name);
      }
    }
  }, [authCtx.loggedIn, authCtx.authLocationData, name]);

  // Mobile/WebView detection
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isWebView = window.webkit?.messageHandlers || 
                     userAgent.includes('wv') || 
                     userAgent.includes('WebView');
    
    setIsMobile(isWebView || /iPhone|iPad|iPod|Android/i.test(userAgent));

    // Handle WebView authentication
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userId = urlParams.get('userId');
    
    if (token && userId && isWebView && !authCtx.loggedIn) {
      // Add loginWithToken method to your AuthContext if it doesn't exist
      if (authCtx.loginWithToken) {
        authCtx.loginWithToken(token, userId);
      }
    }

    // WebView communication setup
    if (window.webkit?.messageHandlers?.mobileApp) {
      window.webkit.messageHandlers.mobileApp.postMessage({
        type: 'mapLoaded'
      });
    }

    // Listen for data from native app
    window.updateMapData = (nativeData) => {
      console.log('Received data from native app:', nativeData);
      
      if (nativeData.authLocationData?.retail?.[0]) {
        setCurrentLocation(nativeData.authLocationData.retail[0]);
      }
      
      if (nativeData.currentData) {
        setCurrentData(nativeData.currentData);
        // Create data structure that matches web app format
        const timeBasedData = {
          [getCurrentTimeInMinutes()]: nativeData.currentData
        };
        setData(timeBasedData);
      }
      
      setLoading(false);
    };

    window.receiveAuthData = (authData) => {
      if (authData.token && authData.userId && authCtx.loginWithToken) {
        authCtx.loginWithToken(authData.token, authData.userId);
      }
    };
  }, [authCtx]);

  // Fetch location data when component mounts (fallback if not from native)
  useEffect(() => {
    const fetchLocationData = async () => {
      if (!authCtx.loggedIn || !currentLocation) {
        console.log('Not ready to fetch data - logged in:', authCtx.loggedIn, 'location:', !!currentLocation);
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching location data for:', currentLocation.name);
        
        // Get current time in minutes
        const timeInMinutes = getCurrentTimeInMinutes(currentLocation.timeZone);
        setCurrentTime(timeInMinutes);
        
        // Get today's date for API call
        const today = moment().tz(currentLocation.timeZone).format("YYYY-MM-DD");
        
        // Import the utility function
        const { getQueuesAPI } = await import('../../util.js');
        
        // Fetch zone occupancy data (same as RetailLocationController)
        const zoneDataPromise = new Promise((resolve, reject) => {
          getQueuesAPI(
            "get-retail-location-daily-data",
            { 
              timezone: currentLocation.timeZone,
              zones: currentLocation.zones.map(zone => zone.id),
              venue: authCtx.venue,
              locationId: currentLocation.id,
              measurement: currentLocation.measurement,
              date: today 
            },
            (data_, error) => {
              if (error) {
                console.error('Error fetching zone data:', error);
                if (error.status === 401) {
                  authCtx.logout();
                }
                reject(error);
              } else {
                console.log('Zone data received:', data_);
                
                // Process the data same way as RetailLocationController
                const zoneDataMap = new Map();
                data_.forEach((dataEntry) => {
                  Object.entries(dataEntry).forEach(([zoneId, zoneData]) => {
                    zoneDataMap.set(parseInt(zoneId), zoneData);
                  });
                });
                
                // Find current time data - get the most recent data point
                const currentTimeData = [];
                currentLocation.zones.forEach(zone => {
                  const zoneData = zoneDataMap.get(zone.id) || [];
                  
                  // Get the most recent data point (last in array)
                  const latestData = zoneData[zoneData.length - 1];
                  
                  currentTimeData.push({
                    id: zone.id,
                    traffic: latestData?.occupancy || 0
                  });
                });
                
                // Calculate totals
                const total = currentTimeData.reduce((sum, item) => sum + item.traffic, 0);
                const perc = Math.round((total / currentLocation.maxOccupancy) * 100);
                
                const formattedData = {
                  total,
                  perc,
                  traffic: currentTimeData
                };
                
                resolve(formattedData);
              }
            },
            authCtx.token
          );
        });
        
        const occupancyData = await zoneDataPromise;
        setCurrentData(occupancyData);
        
        // Create time-based data structure for compatibility
        const timeBasedData = {
          [timeInMinutes]: occupancyData
        };
        setData(timeBasedData);
        
        console.log('Mobile map data loaded successfully:', occupancyData);
        
      } catch (error) {
        console.error('Error fetching mobile map data:', error);
        
        // Create mock data for development/testing
        console.log('Creating mock data for development');
        const mockCurrentData = {
          total: Math.floor(Math.random() * 50) + 20,
          perc: Math.floor(Math.random() * 40) + 10,
          traffic: currentLocation.zones.map(zone => ({
            id: zone.id,
            traffic: Math.floor(Math.random() * 10) + 1
          }))
        };
        
        setCurrentData(mockCurrentData);
        setData({ [getCurrentTimeInMinutes()]: mockCurrentData });
        
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we haven't received data from native app and currentData is null
    if (authCtx.loggedIn && currentLocation && !currentData) {
      fetchLocationData();
    }
  }, [authCtx.loggedIn, currentLocation, name]);

  // Load map component dynamically
  useEffect(() => {
    let mounted = true;

    const loadComponent = async () => {
      if (!currentLocation?.league || !authCtx.venue || !currentLocation?.mapId) {
        console.log('Missing data for component loading:', {
          league: currentLocation?.league,
          venue: authCtx.venue, // Use authCtx.venue instead of currentLocation.venue
          mapId: currentLocation?.mapId
        });
        return;
      }

      try {
        // Use the same path pattern as RetailMap.js
        const componentPath = `../../clients/${authCtx.league}/${authCtx.venue}/maps/${currentLocation.mapId}.js`;
        console.log(`Attempting to load map component from: ${componentPath}`);
        
        const module = await import(`../../clients/${authCtx.league}/${authCtx.venue}/maps/${currentLocation.mapId}.js`);
        console.log('Map component loaded successfully:', module);
        
        if (mounted) {
          setLoadedComponent(() => module.default);
        }
      } catch (error) {
        console.error('Error loading map component:', error);
        console.error('Component path that failed:', `../../clients/${authCtx.league}/${authCtx.venue}/maps/${currentLocation.mapId}.js`);
        
        // Try alternative paths or create a fallback
        try {
          console.log('Trying alternative import path...');
          // Try without .js extension
          const module = await import(`../../clients/${authCtx.league}/${authCtx.venue}/maps/${currentLocation.mapId}`);
          console.log('Alternative path worked:', module);
          
          if (mounted) {
            setLoadedComponent(() => module.default);
          }
        } catch (altError) {
          console.error('Alternative path also failed:', altError);
          
          // Create a simple fallback component
          console.log('Creating fallback component');
          const FallbackComponent = () => (
            <div style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <h3>Map Component Not Found</h3>
                <p>Expected path: {authCtx.league}/{authCtx.venue}/maps/{currentLocation.mapId}</p>
                <p>Please check if the file exists.</p>
              </div>
            </div>
          );
          
          if (mounted) {
            setLoadedComponent(() => FallbackComponent);
          }
        }
      }
    };

    if (currentLocation && authCtx.venue && authCtx.league) {
      loadComponent();
    }

    return () => {
      mounted = false;
    };
  }, [currentLocation, authCtx.venue, authCtx.league]);

  // Helper function to get current time in minutes
  const getCurrentTimeInMinutes = (timeZone = 'America/New_York') => {
    const now = moment().tz(timeZone);
    let minutes = now.hours() * 60 + now.minutes();
    
    // Handle extended day (midnight to 3am next day)
    if (now.hour() >= 0 && now.hour() < 3) {
      minutes += 1440; // Add 24 hours
    }
    
    return minutes;
  };

  // Zone interaction handler
  const handleZoneInteraction = (zoneId) => {
    setHoveredZoneId(zoneId);
    
    if (window.webkit?.messageHandlers?.mobileApp) {
      window.webkit.messageHandlers.mobileApp.postMessage({
        type: 'zoneClicked',
        zoneId: zoneId,
        zoneData: {
          id: zoneId,
          label: findLabelByZoneId(zoneId),
          traffic: currentData?.traffic?.find(zone => zone.id === zoneId)?.traffic || 0
        }
      });
    }
  };

  // Find label by zone ID (simplified version)
  const findLabelByZoneId = (zoneId) => {
    // Check if userZonePreferences exists and has this location
    const locationId = currentLocation?.id?.toString();
    if (authCtx.userZonePreferences && 
        authCtx.userZonePreferences[locationId]) {
      // Find the preference for this zone in the specific location
      const preference = authCtx.userZonePreferences[locationId].find(
        (pref) => pref.zoneId === zoneId
      );
      return preference ? preference.label : currentLocation.defaultZoneLabel;
    }
    return currentLocation?.defaultZoneLabel || 'Merch';
  };

  // Loading state
  if (!authCtx.loggedIn) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <h3>Authentication Required</h3>
          <p>Please log in to view the map.</p>
        </div>
      </div>
    );
  }

  if (loading || !currentLocation) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <Spinner size="large" />
          <p className="mt-3">
            {!currentLocation ? 'Loading location...' : 'Loading data...'}
          </p>
        </div>
      </div>
    );
  }

  if (!loadedComponent) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <Spinner size="large" />
          <p className="mt-3">Loading map component...</p>
          <small className="text-muted">
            Path: {authCtx.league}/{authCtx.venue}/maps/{currentLocation.mapId}
          </small>
        </div>
      </div>
    );
  }

  const MapComponent = loadedComponent;

  return (
    <div 
      style={{ 
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: '#f8f9fa',
        touchAction: 'manipulation',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
    >
      {/* Mobile header */}
      {/* <div 
        style={{ 
          padding: '10px 15px',
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}
      >
        <h4 className="mb-1 text-center">{currentLocation.name}</h4>
        <div className="text-center text-muted">
          <small>
            Total: {currentData?.total || 0} / {currentLocation.maxOccupancy} 
            ({currentData?.perc || 0}% Full)
          </small>
        </div>
      </div> */}

      {/* Map container */}
      <div 
        style={{ 
          height: 'calc(100vh - 80px)',
          width: '100%',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <div style={{ 
          width: '100%', 
          height: '100%',
          maxWidth: '700px',
        }}>
          <MapComponent
            currentLocation={currentLocation}
            currentData={currentData}
            findLabelByZoneId={findLabelByZoneId}
            isAnalyticsView={false}
            hoveredZoneId={hoveredZoneId}
            onZoneHover={handleZoneInteraction}
            isMobile={isMobile}
          />
        </div>
      </div>

      {/* Mobile legend */}
      {/* <div 
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontSize: '12px',
          minWidth: '120px'
        }}
      >
        <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>Occupancy</div>
        <div style={{ display: 'flex', height: '6px', marginBottom: '5px' }}>
          <div style={{ 
            flex: 1, 
            background: 'linear-gradient(to right, rgb(122, 219, 92), rgb(245, 235, 84), rgb(234, 170, 60), rgb(218, 81, 70))' 
          }}></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Low</span>
          <span>High</span>
        </div>
      </div> */}

      {/* Zone tooltip for mobile */}
      {/* {hoveredZoneId && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 2000,
            minWidth: '200px',
            textAlign: 'center'
          }}
          onClick={() => setHoveredZoneId(null)}
        >
          <h5>Zone #{hoveredZoneId}</h5>
          <h4>{findLabelByZoneId(hoveredZoneId)}</h4>
          <h3>
            {currentData?.traffic?.find(zone => zone.id === hoveredZoneId)?.traffic || 0} people
          </h3>
          <small style={{ color: '#666' }}>Tap to close</small>
        </div>
      )} */}
    </div>
  );
};

export default RetailMobileMap;